import { z } from 'zod';
import {
  BaseEntitySchema,
  OffsetQuerySchema,
  SortOrderSchema,
  idField,
  qField,
  titleField,
  updatedAtField,
} from './common';
import { AttachmentInputSchema } from './uploads';

const descriptionField = z.string().trim().min(1).max(500);
const priceField = z.number().int().nonnegative().max(10000);
const stockField = z.number().int().nonnegative().max(10000);
// Images stored in the generic `attachments` table (entity_type 'sku').
const skuImagesField = z.array(AttachmentInputSchema).max(5).optional();

export const PublicSkuSchema = BaseEntitySchema.extend({
  updatedAt: updatedAtField,
  number: z.number().int(),
  title: z.string(),
  description: z.string(),
  price: z.number().int(),
  stock: z.number().int(),
  imageUrls: z.array(z.string()),
  isActive: z.boolean(),
});
export type PublicSku = z.infer<typeof PublicSkuSchema>;

export const SkuListResponseSchema = z.array(PublicSkuSchema);
export type SkuListResponse = z.infer<typeof SkuListResponseSchema>;

export const ADMIN_SKU_SORTS = { CREATED: 'created', PRICE: 'price', STOCK: 'stock' } as const;
export type AdminSkuSort = (typeof ADMIN_SKU_SORTS)[keyof typeof ADMIN_SKU_SORTS];
const ADMIN_SKU_SORT_VALUES = Object.values(ADMIN_SKU_SORTS) as [AdminSkuSort, ...AdminSkuSort[]];
export const AdminSkuSortSchema = z.enum(ADMIN_SKU_SORT_VALUES);

export const AdminSkuListQuerySchema = OffsetQuerySchema.extend({
  q: qField,
  sort: AdminSkuSortSchema.optional(),
  order: SortOrderSchema.optional(),
});
export type AdminSkuListQuery = z.infer<typeof AdminSkuListQuerySchema>;

export const AdminSkuListResponseSchema = z.object({
  skus: z.array(PublicSkuSchema),
  total: z.number().int(),
});
export type AdminSkuListResponse = z.infer<typeof AdminSkuListResponseSchema>;

export const SkuOptionSchema = z.object({
  id: idField,
  number: z.number().int(),
  title: z.string(),
});
export type SkuOption = z.infer<typeof SkuOptionSchema>;

export const SkuOptionListResponseSchema = z.array(SkuOptionSchema);
export type SkuOptionListResponse = z.infer<typeof SkuOptionListResponseSchema>;

export const SKU_SORTS = { NEWEST: 'newest', CHEAP: 'cheap', EXPENSIVE: 'expensive' } as const;
export type SkuSort = (typeof SKU_SORTS)[keyof typeof SKU_SORTS];
const SKU_SORT_VALUES = Object.values(SKU_SORTS) as [SkuSort, ...SkuSort[]];
export const SkuSortSchema = z.enum(SKU_SORT_VALUES);

export const SkuListQuerySchema = z.object({
  q: qField,
  sort: SkuSortSchema.optional(),
});
export type SkuListQuery = z.infer<typeof SkuListQuerySchema>;

export const CreateSkuInputSchema = z.object({
  title: titleField,
  description: descriptionField,
  price: priceField,
  stock: stockField,
  attachments: skuImagesField,
});
export type CreateSkuInput = z.infer<typeof CreateSkuInputSchema>;

export const UpdateSkuInputSchema = z
  .object({
    title: titleField.optional(),
    description: descriptionField.optional(),
    price: priceField.optional(),
    stock: stockField.optional(),
    isActive: z.boolean().optional(),
    attachments: skuImagesField,
  })
  .refine((d) => Object.keys(d).length > 0, { message: 'At least one field is required' });
export type UpdateSkuInput = z.infer<typeof UpdateSkuInputSchema>;

export const CheckoutItemSchema = z.object({
  skuId: idField,
  count: z.number().int().min(1),
});
export type CheckoutItem = z.infer<typeof CheckoutItemSchema>;

export const CheckoutInputSchema = z.object({
  items: z.array(CheckoutItemSchema).min(1),
});
export type CheckoutInput = z.infer<typeof CheckoutInputSchema>;

export const CheckoutResponseSchema = z.object({
  balance: z.number().int(),
  orderId: z.string(),
});
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;
